import { useSelector, shallowEqual } from 'react-redux';

import { FUEL_TYPES, PAYMENT_METHODS } from 'config/app-config';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { usePartner } from 'components/app/routes/usePartner';
import { useMeterType } from 'hooks/useMeterType';

const ELEC_ONLY_PAYMENT_OPTIONS = [
  {
    variables: {
      [FUEL_TYPES.ELECTRIC]: true,
      [FUEL_TYPES.GAS]: false,
      isPrepayElectricityMeter: true,
      isTOUElectricityMeter: false,
      isPrepayGasMeter: null,
      isTOUGasMeter: null
    },
    paymentOptions: [PAYMENT_METHODS.PREPAY]
  },
  {
    variables: {
      [FUEL_TYPES.ELECTRIC]: true,
      [FUEL_TYPES.GAS]: false,
      isPrepayElectricityMeter: false,
      isTOUElectricityMeter: false,
      isPrepayGasMeter: null,
      isTOUGasMeter: null
    },
    paymentOptions: [
      PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
      PAYMENT_METHODS.CHECK
    ]
  },
  {
    variables: {
      [FUEL_TYPES.ELECTRIC]: true,
      [FUEL_TYPES.GAS]: false,
      isPrepayElectricityMeter: false,
      isTOUElectricityMeter: true,
      isPrepayGasMeter: null,
      isTOUGasMeter: null
    },
    paymentOptions: [
      PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
      PAYMENT_METHODS.CHECK,
      PAYMENT_METHODS.PREPAY
    ]
  },
  {
    variables: {
      [FUEL_TYPES.ELECTRIC]: true,
      [FUEL_TYPES.GAS]: false,
      isPrepayElectricityMeter: false,
      isTOUElectricityMeter: false,
      isPrepayGasMeter: null,
      isTOUGasMeter: null
    },
    paymentOptions: [
      PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
      PAYMENT_METHODS.CHECK
    ]
  }
];

const GAS_ONLY_PAYMENT_OPTIONS = [
  {
    variables: {
      [FUEL_TYPES.ELECTRIC]: false,
      [FUEL_TYPES.GAS]: true,
      isPrepayElectricityMeter: null,
      isTOUElectricityMeter: null,
      isPrepayGasMeter: true,
      isTOUGasMeter: false
    },
    paymentOptions: [PAYMENT_METHODS.PREPAY]
  },
  {
    variables: {
      [FUEL_TYPES.ELECTRIC]: false,
      [FUEL_TYPES.GAS]: true,
      isPrepayElectricityMeter: null,
      isTOUElectricityMeter: null,
      isPrepayGasMeter: false,
      isTOUGasMeter: true
    },
    paymentOptions: [
      PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
      PAYMENT_METHODS.CHECK,
      PAYMENT_METHODS.PREPAY
    ]
  },
  {
    variables: {
      [FUEL_TYPES.ELECTRIC]: false,
      [FUEL_TYPES.GAS]: true,
      isPrepayElectricityMeter: null,
      isTOUElectricityMeter: null,
      isPrepayGasMeter: false,
      isTOUGasMeter: false
    },
    paymentOptions: [
      PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
      PAYMENT_METHODS.CHECK
    ]
  }
];

const DUAL_FUEL_PAYMENT_OPTIONS = [
  {
    variables: {
      [FUEL_TYPES.ELECTRIC]: true,
      [FUEL_TYPES.GAS]: true,
      isPrepayElectricityMeter: true,
      isTOUElectricityMeter: false,
      isPrepayGasMeter: true,
      isTOUGasMeter: false
    },
    paymentOptions: [PAYMENT_METHODS.PREPAY]
  },
  {
    variables: {
      [FUEL_TYPES.ELECTRIC]: true,
      [FUEL_TYPES.GAS]: true,
      isPrepayElectricityMeter: false,
      isTOUElectricityMeter: false,
      isPrepayGasMeter: false,
      isTOUGasMeter: false
    },
    paymentOptions: [
      PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
      PAYMENT_METHODS.CHECK
    ]
  },
  {
    variables: {
      [FUEL_TYPES.ELECTRIC]: true,
      [FUEL_TYPES.GAS]: true,
      isPrepayElectricityMeter: false,
      isTOUElectricityMeter: false,
      isPrepayGasMeter: false,
      isTOUGasMeter: true
    },
    paymentOptions: [
      PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
      PAYMENT_METHODS.CHECK
    ]
  },
  {
    variables: {
      [FUEL_TYPES.ELECTRIC]: true,
      [FUEL_TYPES.GAS]: true,
      isPrepayElectricityMeter: false,
      isTOUElectricityMeter: true,
      isPrepayGasMeter: false,
      isTOUGasMeter: true
    },
    paymentOptions: [
      PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
      PAYMENT_METHODS.CHECK,
      PAYMENT_METHODS.PREPAY
    ]
  },
  {
    variables: {
      [FUEL_TYPES.ELECTRIC]: true,
      [FUEL_TYPES.GAS]: true,
      isPrepayElectricityMeter: false,
      isTOUElectricityMeter: true,
      isPrepayGasMeter: false,
      isTOUGasMeter: false
    },
    paymentOptions: [
      PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
      PAYMENT_METHODS.CHECK
    ]
  }
];

const PAYMENT_OPTION_MAPPING = [
  ...ELEC_ONLY_PAYMENT_OPTIONS,
  ...GAS_ONLY_PAYMENT_OPTIONS,
  ...DUAL_FUEL_PAYMENT_OPTIONS
];

export const usePaymentOptions = () => {
  const { isDSJourney } = usePartner();

  const { electric, gas } = useSelector(selectFuelType, shallowEqual);
  const {
    hasElectricityPrepayMeter,
    hasGasPrepayMeter,
    hasElectricityTOUMeter,
    hasGasTOUMeter
  } = useMeterType();

  const toMatch = {
    [FUEL_TYPES.ELECTRIC]: electric,
    [FUEL_TYPES.GAS]: gas,
    isPrepayElectricityMeter: hasElectricityPrepayMeter,
    isTOUElectricityMeter: hasElectricityTOUMeter,
    isPrepayGasMeter: hasGasPrepayMeter,
    isTOUGasMeter: hasGasTOUMeter
  };

  const paymentOptions = PAYMENT_OPTION_MAPPING.reduce(
    (acc, { variables, paymentOptions }) => {
      const isMatch = Object.entries(variables).every(([key, value]) => {
        return !!toMatch[key] === !!value;
      });

      return isMatch ? paymentOptions : acc;
    },
    []
  );

  if (isDSJourney) {
    return paymentOptions.filter((option) => {
      return option !== PAYMENT_METHODS.CHECK;
    });
  }

  return paymentOptions;
};
